'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useLoader } from '@/lib/context/LoaderProvider'
import { useRouter } from 'next/navigation'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import HeroSection from '@/components/landing/HeroSection'
import LeagueBadgesCarousel from '@/components/landing/LeagueBadgesCarousel'
import AdrenalineSection from '@/components/landing/AdrenalineSection'
import StepsToSuccessSection from '@/components/landing/StepsToSuccessSection'
import SponsorsSection from '@/components/landing/SponsorsSection'
import FinalCTASection from '@/components/landing/FinalCTASection'
import LandingFooter from '@/components/landing/LandingFooter'

// Register GSAP plugins
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}

export default function HomePage() {
  const { user, isLoading } = useAuth()
  const router = useRouter()
  const { isLoaderVisible } = useLoader()
  const [showLanding, setShowLanding] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isLoading) return

    if (!user) {
      setShowLanding(true)
      return
    }

    setShowLanding(false)
    router.replace('/home')
  }, [user, isLoading, router])

  useEffect(() => {
    // Only initialize animations when the landing page should be shown AND the loader is hidden
    if (showLanding && !isLoaderVisible && containerRef.current) {
      const ctx = gsap.context(() => {
        // Parallax effect for background orbs
        gsap.to('.landing-page .bg-gradient-to-r', {
          yPercent: -30,
          ease: 'none',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        })
      }, containerRef)

      return () => ctx.revert()
    }
  }, [showLanding, isLoaderVisible])

  // While loading, the layout will show the loader.
  // We can return null here or a minimal placeholder.
  // The loader is handled globally. We just need to wait for it to be hidden.
  if (isLoaderVisible) {
    return null
  }

  // Trust middleware to handle routing - show landing only for unauthenticated users
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-foreground">Redirecting...</p>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`landing-page relative min-h-screen text-foreground transition-colors${showLanding ? ' dark' : ''}`}
      style={{ backgroundColor: 'var(--landing-surface)' }}
    >
      {/* Hero Section */}
      <section className="scroll-mt-20" id="landing-hero">
        <HeroSection />
      </section>

      {/* League Badges Carousel */}
      <section className="scroll-mt-20" id="landing-leagues">
        <LeagueBadgesCarousel />
      </section>

      {/* Adrenaline Features Section */}
      <section className="scroll-mt-20" id="landing-about">
        <AdrenalineSection />
      </section>

      {/* Steps to Success Section */}
      <section className="scroll-mt-20" id="landing-how-to">
        <StepsToSuccessSection />
      </section>

      {/* Sponsors Section */}
      <section className="scroll-mt-20" id="landing-sponsors">
        <SponsorsSection />
      </section>

      {/* Final CTA Section */}
      <FinalCTASection />

      {/* Footer */}
      <LandingFooter />
    </div>
  )
}
